/**
 * /es/blog/:slug — Spanish blog post detail page.
 *
 * Fetches by ES slug from /api/translation/blog-posts/es/:slug. Renders
 * with EsSEO (canonical to ES URL, hreflang en pointing to /blogs/<en_slug>),
 * Article schema in es-US, and dangerouslySetInnerHTML for the translated
 * HTML body (the translator preserves HTML structure per glossary prompt).
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import EsSEO from '@/components/seo/EsSEO';
import SpaRedirect from '@/components/seo/SpaRedirect';
import { registerBlogSlugMapping } from '@/i18n/routeMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://fortworthdental.com';

const SpanishBlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | not_translated | not_found

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    axios
      .get(`${BACKEND_URL}/api/translation/blog-posts/es/${slug}`)
      .then((res) => {
        if (cancelled) return;
        setPost(res.data);
        setStatus('ok');
        // Register ES->EN slug mapping so LangToggle round-trips correctly
        registerBlogSlugMapping(res.data.es_slug, res.data.en_slug);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg = e.response?.data?.detail || '';
        const code = e.response?.status;
        if (msg.includes('not yet translated')) setStatus('not_translated');
        else if (code === 404) setStatus('not_found');   // genuine 404 -> noindex
        else setStatus('transient');                       // 5xx/network -> NO noindex
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (status === 'loading' || status === 'transient') {
    return (
      <section className="py-24 bg-white text-center">
        <p className="text-gray-500">Cargando artículo...</p>
      </section>
    );
  }

  if (status === 'not_translated') {
    return (
      <>
        <EsSEO
          title="Artículo en traducción – Fort Worth Dental"
          description="Este artículo aún no ha sido traducido al español."
          canonical={`/es/blog/${slug}`}
          hreflangEn={`/blogs/${slug}`}
          noindex
        />
        <section className="py-24 bg-white text-center max-w-xl mx-auto px-4">
          <p className="text-2xl font-bold text-amber-900 mb-3">📝 En traducción</p>
          <p className="text-gray-700 mb-6">
            Este artículo aún no ha sido traducido al español. Mientras tanto, puedes leerlo en inglés.
          </p>
          <Link to={`/blogs/${slug}`} className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-6 py-3 rounded-md">
            Leer en Inglés
          </Link>
        </section>
      </>
    );
  }

  if (status === 'not_found' || !post) {
    // definitive 404 only (deleted/nonexistent post) -> clean redirect to the
    // ES blog index instead of a 200+noindex soft-404.
    return <SpaRedirect to="/es/blog" message="Redirigiendo al blog…" />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    inLanguage: 'es-US',
    headline: post.title,
    description: post.summary,
    image: post.featured_image,
    author: { '@type': 'Person', name: post.author || 'Fort Worth Dental' },
    publisher: {
      '@type': 'Organization',
      name: 'Fort Worth Dental',
      logo: { '@type': 'ImageObject', url: `${SITE}/logo-fwd.png` },
    },
    datePublished: post.published_at,
    dateModified: post.translated_at || post.published_at,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/es/blog/${post.es_slug}` },
  };

  return (
    <>
      <EsSEO
        title={post.seo_title || post.title}
        description={post.seo_description || post.summary}
        canonical={`/es/blog/${post.es_slug}`}
        hreflangEn={`/blogs/${post.en_slug}`}
        ogImage={post.featured_image}
        schema={articleSchema}
      />

      <article className="bg-white">
        {post.featured_image && (
          <div className="w-full h-64 sm:h-96 bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 overflow-hidden relative flex items-center justify-center">
            <img
              src={post.featured_image}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <svg className="w-20 h-20 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 13.5h3.375m-3.375 0L9.75 18M3.375 7.5h17.25c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125H3.375A1.125 1.125 0 012.25 18.375v-9.75c0-.621.504-1.125 1.125-1.125z" />
              </svg>
            </div>
          </div>
        )}

        <div className="container mx-auto px-4 max-w-3xl py-12">
          <nav aria-label="breadcrumb" className="text-sm text-gray-500 mb-4">
            <Link to="/es" className="hover:text-teal-600">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/es/blog" className="hover:text-teal-600">Noticias Dentales</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{post.title}</span>
          </nav>

          {post.category && (
            <span className="inline-block text-xs font-semibold text-teal-700 bg-teal-50 px-3 py-1 rounded-full mb-3">
              {post.category}
            </span>
          )}

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
            {post.author && <span>Por {post.author}</span>}
            {post.published_at && (
              <span>
                {new Date(post.published_at).toLocaleDateString('es-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>

          {post.summary && (
            <p className="text-lg text-gray-700 leading-relaxed mb-8 italic border-l-4 border-teal-500 pl-4">
              {post.summary}
            </p>
          )}

          <div
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-a:text-teal-600 prose-strong:text-gray-900"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="es-blog-content"
          />

          <div className="mt-12 border-t border-gray-200 pt-8">
            <Link to="/es/blog" className="text-teal-600 hover:underline font-semibold">
              ← Ver todos los artículos
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default SpanishBlogPost;
